/*
  Reads everything the support team needs and the user should not have to type: where they
  were, when, in which browser, and for which customer. This is the only layer that touches
  the clock, the route, and the window, which is what keeps src/lib/feedbackMail.ts pure.
*/

import { FeedbackContext, pageLabel } from '@/lib/feedbackMail'
import { stripOAuthParams } from '@/lib/scrubUrl'
import Bowser from 'bowser'
import { useRoute } from 'vue-router'
import { useCurrentCustomer } from './useCurrentCustomer'

function describeBrowser(): string {
  const browser = Bowser.getParser(window.navigator.userAgent)
  const name = browser.getBrowserName() || 'Unknown browser'
  const version = browser.getBrowserVersion() || 'unknown version'
  const os = browser.getOSName() || 'unknown OS'

  return `${name} ${version} on ${os}`
}

export function useFeedbackContext() {
  const route = useRoute()
  const { customer } = useCurrentCustomer()

  /*
    Called when the dialog opens, not on every route change, so the widget costs nothing
    while it is closed and the timestamp belongs to the message rather than to the session.
  */
  function captureContext(): FeedbackContext {
    const customerName = customer.value?.name?.trim()

    return {
      pageLabel: pageLabel(route ?? { path: window.location.pathname }),
      /*
        The dialog can be opened on the Auth0 callback URL (/?code=…&state=…), before the
        router has rewritten it. buildBody copies this value into a mail the user sends to a
        shared mailbox, so the authorization code must not survive the capture. The asset id
        in the path does survive: the person triaging the ticket needs it to reach the same
        page, and it is not a secret.
      */
      pageUrl: stripOAuthParams(window.location.href),
      sentAt: new Date().toISOString(),
      browser: describeBrowser(),
      // A super admin has no customer. Absent, not empty — buildBody drops the line.
      customerName: customerName || undefined
    }
  }

  return { captureContext }
}
